import { useControlledState } from '@ixigo/iui/utils';
import cn from '@repo/shared/utils/cn';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { WheelPicker, WheelPickerWrapper } from '../wheel-picker';
import { ActiveBorder } from './ActiveBorder';
import { useWheelTempSelection } from './hooks/use-wheel-temp-selection';
import { getClasses } from './styles';
import type { DatePickerProps } from './types';
import {
	buildDateWindowOptions,
	buildDayOptions,
	buildMonthOptions,
	buildYearOptions,
	clampDate,
	getDaysInMonth,
	getDefaultDate,
	startOfDay,
} from './utils';

// Sliding-window behaviour configuration
const WINDOW_HALF = 20; // ±20 days
const RECENTER_AT = 0.75; // 75 % towards either edge
const MS_PER_DAY = 86_400_000;

export function DatePicker({
	defaultValue,
	value,
	onChange,
	visibleRows = 9,
	selectionMode = 'segmented',
	min: minProp,
	max: maxProp,
	className,
	classes: classNames,
}: DatePickerProps) {
	const [min, max] = useMemo(() => {
		// remove time component from min and max and return min and max as Date objects
		return [
			minProp ? startOfDay(new Date(minProp)) : undefined,
			maxProp ? startOfDay(new Date(maxProp)) : undefined,
		];
	}, [minProp, maxProp]);

	const stableValue = useMemo(() => {
		return value ? startOfDay(new Date(value)) : undefined;
	}, [value]);
	const [anchorDate, setAnchorDate] = useControlledState<Date>({
		controlled: stableValue,
		default: defaultValue
			? startOfDay(new Date(defaultValue))
			: startOfDay(getDefaultDate(min, max)),
	});

	const classes = getClasses(classNames);

	const wheelVisibleCount = Math.max(8, Math.floor((visibleRows - 1) / 2) * 4);

	const emit = (date: Date) => {
		setAnchorDate(date);
		onChange?.(startOfDay(date));
	};

	const [slidingAnchorDate, setSlidingAnchorDate] = useState(anchorDate);

	useEffect(() => {
		if (selectionMode !== 'composite') return;

		setSlidingAnchorDate((prev) => {
			const diffDays =
				Math.abs(anchorDate.getTime() - prev.getTime()) / MS_PER_DAY;
			if (diffDays >= WINDOW_HALF * RECENTER_AT) {
				// Re-centre the sliding window in a low-priority update
				return anchorDate;
			}
			return prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [anchorDate, selectionMode]);

	const deferredAnchorDate = useDeferredValue(slidingAnchorDate);
	const dateOptions = useMemo(() => {
		if (selectionMode !== 'composite') return [];
		// Always build a sliding 2*WINDOW_HALF-day window centred on anchorDate. This avoids generating
		// every date in a large range while still giving users enough context.
		return buildDateWindowOptions(deferredAnchorDate, WINDOW_HALF);
	}, [deferredAnchorDate, selectionMode]);

	// Year bounds for segmented mode. If no max is provided, allow future years up to 2100.
	const minYear = min ? new Date(min).getFullYear() : 1900;
	const maxYear = max ? new Date(max).getFullYear() : 2100;

	const { tempValueByField, tryCommit } = useWheelTempSelection<
		'year' | 'month' | 'day' | 'date'
	>();

	if (selectionMode === 'composite') {
		return (
			<WheelPickerWrapper className={cn(classes.root, className, 'min-w-32')}>
				<WheelPicker
					options={dateOptions}
					value={tempValueByField.date ?? anchorDate.toISOString()}
					optionItemHeight={50}
					visibleCount={12}
					classNames={classes}
					onValueChange={(isoDateString: string) => {
						const selectedDate = new Date(isoDateString);
						const bounded = clampDate(selectedDate, min, max);
						const isValid = selectedDate.getTime() === bounded.getTime();
						tryCommit({
							field: 'date',
							isValid: () => isValid,
							commit: () => emit(bounded),
							displayValue: isoDateString,
						});
					}}
				/>
				<ActiveBorder optionHeight={50} />
			</WheelPickerWrapper>
		);
	}

	// segmented
	return (
		<WheelPickerWrapper className={cn(classes.root, 'min-w-300px', className)}>
			{/* Year */}
			<WheelPicker
				options={buildYearOptions(minYear, maxYear)}
				value={tempValueByField.year ?? anchorDate.getFullYear().toString()}
				optionItemHeight={50}
				visibleCount={wheelVisibleCount}
				classNames={classes}
				onValueChange={(val: string) => {
					const nextYear = Number(val);
					const days = getDaysInMonth(nextYear, anchorDate.getMonth());
					const nextDay = Math.min(anchorDate.getDate(), days);
					const selectedDate = new Date(anchorDate);
					selectedDate.setFullYear(nextYear, selectedDate.getMonth(), nextDay);
					const bounded = clampDate(selectedDate, min, max);

					const isValid = selectedDate.getTime() === bounded.getTime();
					tryCommit({
						field: 'year',
						isValid: () => isValid,
						commit: () => emit(bounded),
						displayValue: val,
					});
				}}
			/>
			{/* Month */}
			<WheelPicker
				options={buildMonthOptions()}
				value={tempValueByField.month ?? (anchorDate.getMonth() + 1).toString()}
				optionItemHeight={50}
				visibleCount={wheelVisibleCount}
				classNames={classes}
				infinite
				onValueChange={(val: string) => {
					const nextMonthIndex = Number(val) - 1;
					const days = getDaysInMonth(anchorDate.getFullYear(), nextMonthIndex);
					const nextDay = Math.min(anchorDate.getDate(), days);
					const selectedDate = new Date(anchorDate);
					selectedDate.setMonth(nextMonthIndex, nextDay);
					const bounded = clampDate(selectedDate, min, max);

					const isValid = selectedDate.getTime() === bounded.getTime();
					tryCommit({
						field: 'month',
						isValid: () => isValid,
						commit: () => emit(bounded),
						displayValue: val,
					});
				}}
			/>
			{/* Day */}
			<WheelPicker
				options={buildDayOptions(
					getDaysInMonth(anchorDate.getFullYear(), anchorDate.getMonth()),
				)}
				value={tempValueByField.day ?? anchorDate.getDate().toString()}
				optionItemHeight={50}
				visibleCount={wheelVisibleCount}
				classNames={classes}
				infinite
				onValueChange={(val: string) => {
					const nextDay = Number(val);
					const selectedDate = new Date(anchorDate);
					selectedDate.setDate(nextDay);
					const bounded = clampDate(selectedDate, min, max);

					const isValid = selectedDate.getTime() === bounded.getTime();
					tryCommit({
						field: 'day',
						isValid: () => isValid,
						commit: () => emit(bounded),
						displayValue: val,
					});
				}}
			/>
			<ActiveBorder optionHeight={50} />
		</WheelPickerWrapper>
	);
}
